import { withDefaults } from '@warp-drive/legacy/model/migration-support';

/**
 * The Payment Operations rate worklist (tio-api#8187 / #10070) — read-only.
 *
 * One row is one thing needing a human decision about one merchant↔network-merchant pair's fee
 * rate. Written by detection and resolved by a ruling; nothing here is client-writable, and the
 * API refuses both creation and a PATCH to `state`. A dismissal goes through its own admin
 * command endpoint, not through this resource.
 */
const PcsRateProposalSchema = withDefaults({
  type: 'pcs-rate-proposal',
  fields: [
    {
      // The program dot the ruling is keyed by. Rates are per program, so a proposal never
      // speaks for another one.
      kind: 'attribute',
      name: 'program',
    },
    {
      // Why a human is being asked, as a PCS.RateProposalKind dot: NEW_PATTERN, VARIANCE,
      // ROLE_CHANGE, CONFLICT, or PAIRING_DISAGREEMENT. Each reads differently on screen.
      kind: 'attribute',
      name: 'kind',
    },
    {
      // OPEN → RULED | DISMISSED. A resolved row keeps its reason and who resolved it.
      kind: 'attribute',
      name: 'state',
    },
    {
      // What the swipes showed, as a 4-dp decimal fraction. Null when the shape of the swipe
      // could not divide — a single authorization observes a role and no rate at all.
      kind: 'attribute',
      name: 'observedCombinedRate',
    },
    {
      // What was ruled at the time this was raised. Null means the pair was unruled then, which
      // is what makes a NEW_PATTERN different from a VARIANCE.
      kind: 'attribute',
      name: 'ruledCombinedRate',
    },
    {
      kind: 'attribute',
      name: 'observedRole',
    },
    {
      kind: 'attribute',
      name: 'ruledRole',
    },
    {
      // Integer cents, both of them. Variance is judged in cents rather than rates, because
      // integer-cent fee legs quantise the rate (25¢ on $9.99 is 2.5025%, not 2.5%).
      kind: 'attribute',
      name: 'observedFeeAmount',
    },
    {
      kind: 'attribute',
      name: 'observedChargeAmount',
    },
    {
      // How many usages support this one row. Detection collapses repeat observations onto the
      // open row rather than stacking a hundred identical items, so this is the weight of the
      // evidence, not a duplicate count.
      kind: 'attribute',
      name: 'observationCount',
    },
    {
      kind: 'attribute',
      name: 'firstObservedAt',
    },
    {
      kind: 'attribute',
      name: 'lastObservedAt',
    },
    {
      kind: 'attribute',
      name: 'resolvedAt',
    },
    {
      // Why it was dismissed. Required by the dismissal endpoint, because a permanent
      // suppression with no reason cannot be reviewed later.
      kind: 'attribute',
      name: 'resolutionNote',
    },
    {
      // The ruling that resolved this, when one did. Read the batch itself through
      // pcs-rate-ruling-batch for the cover choice and the numbers the operator was shown.
      kind: 'attribute',
      name: 'rulingBatchId',
    },
    {
      kind: 'attribute',
      name: 'createdAt',
    },
    {
      kind: 'attribute',
      name: 'updatedAt',
    },
    {
      // Preloaded by the API so a queue page names every merchant without a request per row.
      kind: 'belongsTo',
      name: 'pcsMerchantNetworkMerchant',
      type: 'pcs-merchant-network-merchant',
      options: { async: false, inverse: null },
    },
    {
      // The other pair, when a swipe's legs sat at two network merchants. Both must end up
      // carrying the same combined rate — only the rate is shared, never the role — so the queue
      // has to name it rather than let someone rule half a swipe and walk away.
      kind: 'belongsTo',
      name: 'counterpartMerchantNetworkMerchant',
      type: 'pcs-merchant-network-merchant',
      options: { async: false, inverse: null },
    },
    {
      // The usage that produced the latest observation — evidence, so a reviewer is not left
      // hunting for the swipe.
      kind: 'belongsTo',
      name: 'pcsLogicalCardUsage',
      type: 'pcs-logical-card-usage',
      options: { async: false, inverse: null },
    },
  ],
});

export default PcsRateProposalSchema;
